/**
 * Modal to choose which delivery dates use this address.
 * Uses subscription available dates; preselected dates come from subscription particulars.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, Alert } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { BottomSheetPopup } from '../../components/common/BottomSheetPopup';
import { Clickable } from '../../components/common/Clickable';
import { ButtonLoader } from '../../components/load-main/ButtonLoader';
import moment from 'moment';
import { appTheme, useThemeColors, type ThemeColors } from '../../shared/theme';
import {
  createStackedFooterPrimaryLabelStyle,
  createStackedFooterPrimarySurfaceStyle,
  createStackedFooterSecondaryLabelStyle,
  createStackedFooterSecondarySurfaceStyle,
} from '../../shared/theme/sharedStyles';
import { createSheetFooterDividerStyle } from '../../shared/theme/styles/sheets';
import { useTranslation, getLocale, formatNumber, getLocalizedWeekdaysShort } from '../../shared/i18n';
import { horizontalLtrDirectionStyle } from '../../shared/i18n/rtl';
import { CalendarMonthNavRow, CalendarWeekdayStrip } from '../../components/ui';
import { setDeliveryAddressDates, type DeliveryAddressRequest } from '../../data/domains/addresses';
import type { SubscriptionData } from '../../data/domains/subscription';
import { formatMomentForApiYmd, normalizeCalendarDate } from '../../shared/utils/dateUtils';
import { ensureAsciiDateString } from '../../shared/utils/apiParams';

export interface AddressDeliveryDatesModalProps {
  visible: boolean;
  onClose: (refreshNeeded: boolean) => void;
  address: { id: number; name?: string };
  activeSubscriptions: SubscriptionData[];
  availableDates: string[];
  presetDates: string[];
  customerId: number;
}

interface CalendarDay {
  date: string;
  dayOfMonth: number;
  isDisabled: boolean;
  isPadding: boolean;
}

function generateAddressRequests(
  subscriptions: SubscriptionData[],
  selectedDates: string[],
  customerId: number,
  addressId: number,
): DeliveryAddressRequest[] {
  const requests: DeliveryAddressRequest[] = [];

  for (const sub of subscriptions) {
    const subId = sub.id ?? (sub as any).customer_subscription_id;
    if (!subId || !sub.start_date || !sub.end_date) {continue;}

    const startStr = formatMomentForApiYmd(moment(sub.start_date));
    const endStr = formatMomentForApiYmd(moment(sub.end_date));

    const validDates = selectedDates.filter(d => {
      const ascii = ensureAsciiDateString(d) || normalizeCalendarDate(d);
      const m = moment(ascii, 'YYYY-MM-DD', true);
      return m.isValid() && m.isSameOrAfter(startStr, 'day') && m.isSameOrBefore(endStr, 'day');
    });

    if (validDates.length > 0) {
      requests.push({
        customer_id: customerId,
        customer_subscription_id: subId,
        dates: validDates.map(d => ensureAsciiDateString(d) || normalizeCalendarDate(d)).filter(Boolean),
        address_id: addressId,
      });
    }
  }

  return requests;
}

function createAddressDeliveryDatesModalStyles(c: ThemeColors) {
  return StyleSheet.create({
    sheetBody: {
      paddingHorizontal: appTheme.spacing.md,
      paddingBottom: appTheme.spacing.sm,
    },
    subtitle: {
      ...appTheme.typography.caption,
      color: c.textSecondary,
    },
    weekDaysRow: {
      flexDirection: 'row',
      paddingHorizontal: appTheme.spacing.sm,
      paddingVertical: appTheme.spacing.sm,
      backgroundColor: c.surface,
      ...horizontalLtrDirectionStyle,
    },
    weekDayCell: {
      flex: 1,
      alignItems: 'center',
    },
    weekDayText: {
      ...appTheme.typography.caption,
      color: c.textSecondary,
      fontWeight: '600',
    },
    calendarScroll: {
      flex: 1,
      minHeight: 0,
      maxHeight: 340,
    },
    calendarGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      paddingHorizontal: appTheme.spacing.sm,
      paddingVertical: appTheme.spacing.xs,
      paddingBottom: appTheme.spacing.md,
      ...horizontalLtrDirectionStyle,
    },
    dayCellWrap: {},
    dayCell: {
      width: '14.28%',
      aspectRatio: 1,
      alignItems: 'center',
      justifyContent: 'center',
      padding: appTheme.spacing.xs,
      minHeight: 36,
    },
    dayCellDisabled: {
      opacity: 0.35,
    },
    dayCellSelected: {
      backgroundColor: c.primary,
      borderRadius: appTheme.borderRadius.pill,
    },
    dayText: {
      ...appTheme.typography.bodySmall,
      color: c.text,
      textAlign: 'center',
    },
    dayTextDisabled: {
      color: c.textLight,
    },
    dayTextSelected: {
      color: c.onPrimary,
      fontWeight: '600',
    },
    footer: {
      paddingHorizontal: appTheme.spacing.md,
      gap: appTheme.spacing.sm,
      ...createSheetFooterDividerStyle(c),
    },
    footerSummary: {
      alignItems: 'center',
    },
    summaryText: {
      ...appTheme.typography.caption,
      color: c.textSecondary,
    },
    confirmClickable: { alignSelf: 'stretch' },
    confirmClickableInner: { alignSelf: 'stretch' },
    buttonDisabled: {
      opacity: 0.5,
    },
  });
}

type AddressDeliveryDatesModalStyles = ReturnType<typeof createAddressDeliveryDatesModalStyles>;

const DayCell = React.memo(function DayCell({
  day,
  is_selected,
  on_press,
  cellStyles,
}: {
  day: CalendarDay;
  is_selected: boolean;
  on_press: (d: CalendarDay) => void;
  cellStyles: AddressDeliveryDatesModalStyles;
}) {
  const handle_press = useCallback(() => on_press(day), [day, on_press]);

  if (day.isPadding) {
    return <View style={cellStyles.dayCell} />;
  }

  return (
    <Clickable
      onPress={handle_press}
      disabled={day.isDisabled}
      style={cellStyles.dayCellWrap}
      accessibilityLabel={day.date}
      accessibilityRole="button">
      <View style={[cellStyles.dayCell, day.isDisabled && cellStyles.dayCellDisabled, is_selected && cellStyles.dayCellSelected]}>
        <Text style={[cellStyles.dayText, day.isDisabled && cellStyles.dayTextDisabled, is_selected && cellStyles.dayTextSelected]}>
          {formatNumber(day.dayOfMonth)}
        </Text>
      </View>
    </Clickable>
  );
});

export const AddressDeliveryDatesModal: React.FC<AddressDeliveryDatesModalProps> = ({
  visible,
  onClose,
  address,
  activeSubscriptions: activeSubscriptionsProp,
  availableDates: availableDatesProp,
  presetDates: presetDatesProp,
  customerId,
}) => {
  const insets = useSafeAreaInsets();
  const { t } = useTranslation();
  const themeColors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createAddressDeliveryDatesModalStyles(themeColors), [themeColors]);
  const confirm_primary_cta = useMemo(
    () => ({
      surface: createStackedFooterPrimarySurfaceStyle(themeColors),
      label: createStackedFooterPrimaryLabelStyle(themeColors),
    }),
    [themeColors],
  );
  const cancel_secondary_cta = useMemo(
    () => ({
      surface: createStackedFooterSecondarySurfaceStyle(themeColors),
      label: createStackedFooterSecondaryLabelStyle(themeColors),
    }),
    [themeColors],
  );
  const [currentMonth, setCurrentMonth] = useState(moment());
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const activeSubscriptions = Array.isArray(activeSubscriptionsProp) ? activeSubscriptionsProp : [];
  const availableDates = Array.isArray(availableDatesProp) ? availableDatesProp : [];
  const presetDates = Array.isArray(presetDatesProp) ? presetDatesProp : [];

  const locale = getLocale();
  const week_days = useMemo(() => getLocalizedWeekdaysShort(locale), [locale]);

  useEffect(() => {
    if (visible) {
      setSelectedDates(
        presetDates
          .map(d => ensureAsciiDateString(d) || normalizeCalendarDate(d))
          .filter(Boolean),
      );
      setCurrentMonth(moment());
    }
  }, [visible, presetDates]);

  const available_date_set = useMemo(() => {
    const s = new Set<string>();
    for (const d of availableDates) {
      const n = ensureAsciiDateString(d) || normalizeCalendarDate(d);
      if (n) {
        s.add(n);
      }
    }
    return s;
  }, [availableDates]);

  const calendarDays = useMemo(() => {
    const days: CalendarDay[] = [];
    const startOfMonth = currentMonth.clone().startOf('month');
    const endOfMonth = currentMonth.clone().endOf('month');
    const today = moment().startOf('day');
    const firstDayOfWeek = startOfMonth.day();

    for (let i = 0; i < firstDayOfWeek; i++) {
      const paddingDate = startOfMonth.clone().subtract(firstDayOfWeek - i, 'days');
      days.push({
        date: formatMomentForApiYmd(paddingDate),
        dayOfMonth: paddingDate.date(),
        isDisabled: true,
        isPadding: true,
      });
    }

    let day = startOfMonth.clone();
    while (day.isSameOrBefore(endOfMonth, 'day')) {
      // ASCII YMD — never use raw format under ar locale (Eastern digits break includes()).
      const date_str = formatMomentForApiYmd(day);
      const is_past = day.isBefore(today, 'day');
      const is_available = available_date_set.has(date_str);
      const is_disabled = is_past || !is_available;

      days.push({
        date: date_str,
        dayOfMonth: day.date(),
        isDisabled: is_disabled,
        isPadding: false,
      });
      day.add(1, 'day');
    }

    return days;
  }, [currentMonth, available_date_set]);

  const canNavigatePrev = useMemo(() => {
    const prev_month = currentMonth.clone().subtract(1, 'month');
    return prev_month.isSameOrAfter(moment().startOf('month'));
  }, [currentMonth]);

  const canNavigateNext = useMemo(() => {
    if (availableDates.length === 0) {return false;}
    const last_raw = availableDates[availableDates.length - 1];
    const last_ascii = ensureAsciiDateString(last_raw) || normalizeCalendarDate(last_raw);
    const last_available = moment(last_ascii, 'YYYY-MM-DD', true);
    if (!last_available.isValid()) {
      return false;
    }
    return currentMonth.clone().add(1, 'month').startOf('month').isSameOrBefore(last_available, 'month');
  }, [currentMonth, availableDates]);

  const handlePrevMonth = useCallback(() => {
    if (canNavigatePrev) {setCurrentMonth(prev => prev.clone().subtract(1, 'month'));}
  }, [canNavigatePrev]);

  const handleNextMonth = useCallback(() => {
    if (canNavigateNext) {setCurrentMonth(prev => prev.clone().add(1, 'month'));}
  }, [canNavigateNext]);

  const toggleDateSelection = useCallback((day: CalendarDay) => {
    if (day.isDisabled || day.isPadding) {return;}
    setSelectedDates(prev =>
      prev.includes(day.date) ? prev.filter(d => d !== day.date) : [...prev, day.date],
    );
  }, []);

  const handleConfirm = useCallback(async () => {
    const requests = generateAddressRequests(
      activeSubscriptions,
      selectedDates,
      customerId,
      address.id,
    );

    if (requests.length === 0) {
      Alert.alert(t('common.error'), t('address.deliveryDatesNoSubscription'));
      return;
    }

    setIsSubmitting(true);
    try {
      await setDeliveryAddressDates(requests);
      onClose(true);
    } catch (err: any) {
      Alert.alert(t('common.error'), err?.message ?? t('address.deliveryDatesError'));
    } finally {
      setIsSubmitting(false);
    }
  }, [activeSubscriptions, selectedDates, customerId, address.id, onClose, t]);

  const handleClose = useCallback(() => onClose(false), [onClose]);

  const selected_count = selectedDates.length;
  const can_confirm = selected_count > 0 && !isSubmitting;
  const confirm_label =
    selected_count === 0
      ? t('address.confirmDeliveryDates')
      : t('address.confirmDeliveryDatesCount').replace('{{count}}', String(selected_count));

  return (
    <BottomSheetPopup
      visible={visible}
      onClose={handleClose}
      onRequestClose={handleClose}
      title={t('address.deliveryDatesTitle')}
      showDragHandle
      animationType="slide"
      sheetStyle={StyleSheet.flatten([{ maxHeight: '88%' }, { paddingBottom: Math.max(insets.bottom, appTheme.spacing.md) }])}
      padBottomSafeArea={false}>
      <View style={styles.sheetBody}>
        <Text style={styles.subtitle} numberOfLines={2}>
          {t('address.deliveryDatesSubtitle')}: {address.name || t('address.addressFallback')}
        </Text>
      </View>
      <CalendarMonthNavRow
        currentMonth={currentMonth}
        canNavigatePrev={canNavigatePrev}
        canNavigateNext={canNavigateNext}
        onPrevMonth={handlePrevMonth}
        onNextMonth={handleNextMonth}
        previousMonthLabel={t('calendar.previousMonth')}
        nextMonthLabel={t('calendar.nextMonth')}
      />
      <CalendarWeekdayStrip labels={week_days} rowStyle={styles.weekDaysRow} cellStyle={styles.weekDayCell} textStyle={styles.weekDayText} />
      <ScrollView
        style={styles.calendarScroll}
        contentContainerStyle={styles.calendarGrid}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}>
        {calendarDays.map((day, index) => (
          <DayCell
            key={day.isPadding ? `pad-${index}` : day.date}
            day={day}
            is_selected={selectedDates.includes(day.date)}
            on_press={toggleDateSelection}
            cellStyles={styles}
          />
        ))}
      </ScrollView>
      <View style={styles.footer}>
        <View style={styles.footerSummary}>
          <Text style={styles.summaryText}>{t('address.deliveryDatesHint')}</Text>
        </View>
        <Clickable
          onPress={handleConfirm}
          disabled={!can_confirm}
          style={styles.confirmClickable}
          contentStyle={styles.confirmClickableInner}
          accessibilityLabel={confirm_label}
          accessibilityRole="button">
          <View style={[confirm_primary_cta.surface, !can_confirm && styles.buttonDisabled, { justifyContent: 'center', minHeight: 52 }]}>
            {isSubmitting ? (
              <ButtonLoader size={18} color={themeColors.onPrimary} />
            ) : (
              <Text style={confirm_primary_cta.label}>{confirm_label}</Text>
            )}
          </View>
        </Clickable>
        <Clickable onPress={handleClose} accessibilityLabel={t('common.cancel')} accessibilityRole="button" contentStyle={{ alignSelf: 'stretch' }}>
          <View style={[cancel_secondary_cta.surface, { justifyContent: 'center', minHeight: 52 }]}>
            <Text style={cancel_secondary_cta.label}>{t('common.cancel')}</Text>
          </View>
        </Clickable>
      </View>
    </BottomSheetPopup>
  );
};

export default AddressDeliveryDatesModal;
